import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { agencyService } from "../../../services/agency.service";
import { PaymentInfoForm, PaymentMethodData } from "../../../components/affiliate/PaymentInfoForm";

interface AgencyPaymentInfoForm {
  paymentMethodsData: PaymentMethodData[];
}

export function PaymentDetails() {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<AgencyPaymentInfoForm>({
    paymentMethodsData: []
  });
  const [error, setError] = useState<string>("");

  // Fetch agency profile to get payment methods
  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["agency-profile"],
    queryFn: () => agencyService.getProfile(),
  });

  useEffect(() => {
    if (profile?.paymentMethods) {
      // Convert agency payment methods to the format expected by PaymentInfoForm
      const paymentMethodsData: PaymentMethodData[] = profile.paymentMethods.map(pm => ({
        paymentMethodId: pm.paymentMethodId,
        fieldValues: Object.entries(pm.fieldData).map(([fieldName, value]) => ({
          fieldName,
          value: value as string
        }))
      }));
      
      setFormData({
        paymentMethodsData
      });
    }
  }, [profile]);

  const handlePaymentMethodsDataChange = (paymentMethodsData: PaymentMethodData[]) => {
    setFormData({
      paymentMethodsData
    });
    setError("");
  };

  const handleSubmit = async () => {
    // Count filled payment methods (methods with at least one non-empty field)
    const filledMethodsCount = formData.paymentMethodsData.filter(method => 
      method.fieldValues.some(field => field.value && field.value.trim() !== '')
    ).length;
    
    if (filledMethodsCount < 1) {
      setError("Fill in at least one payment method");
      return;
    }
    
    setIsLoading(true);

    try {
      // Prepare payment methods data for update
      const paymentMethodsToUpdate = [];
      
      // Process each payment method that has data
      for (const method of formData.paymentMethodsData) {
        // Filter out empty field values
        const validFieldValues = method.fieldValues.filter(field => 
          field.value && field.value.trim() !== ''
        );
        
        // Only include payment methods with valid field values
        if (validFieldValues.length > 0) {
          paymentMethodsToUpdate.push({
            paymentMethodId: method.paymentMethodId,
            fieldValues: validFieldValues
          });
        }
      }
      
      // Update payment methods using agency service
      if (paymentMethodsToUpdate.length > 0) {
        await agencyService.updatePaymentMethods(paymentMethodsToUpdate);
        
        // Refresh profile to get updated payment methods
        queryClient.invalidateQueries({ queryKey: ["agency-profile"] });
      }

      toast.success("Payment information updated successfully!");
    } catch (err) {
      console.error("Failed to update payment info:", err);
      toast.error("Failed to update payment information. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-1">Payment Details</h2>
      <p className="text-muted-foreground mb-6">
        Where Reveshare sends your commissions. One method is enough.
      </p>

      <PaymentInfoForm
        paymentMethodsData={formData.paymentMethodsData}
        onChangePaymentMethodsData={handlePaymentMethodsDataChange}
        showTitle={false}
        showSaveButton={true}
        onSave={handleSubmit}
        isLoading={isLoading || isLoadingProfile}
        error={error}
        minMethods={1}
      />
    </div>
  );
}